import { create } from 'zustand';
import { getEntitlements } from '../api/billing';
import type { EntitlementsResponse } from '../api/billing';

/**
 * Entitlement store — caches the user's global and per-certification access rights.
 *
 * Data is fetched from GET /api/entitlements and cached with a 5-minute staleness window.
 * Components should use the derived hooks below rather than reading raw state.
 */

interface EntitlementState {
  data: EntitlementsResponse | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;

  fetchEntitlements: () => Promise<void>;
  reset: () => void;
}

/** Staleness window: re-fetch if data is older than 5 minutes. */
const STALE_TIME_MS = 5 * 60 * 1000;

export const useEntitlementStore = create<EntitlementState>((set, get) => ({
  data: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchEntitlements: async () => {
    const { lastFetched, isLoading } = get();

    // Skip if already loading or data is still fresh
    if (isLoading) return;
    if (lastFetched && Date.now() - lastFetched < STALE_TIME_MS) return;

    set({ isLoading: true, error: null });
    try {
      const data = await getEntitlements();
      set({ data, isLoading: false, lastFetched: Date.now() });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Failed to fetch entitlements';
      set({ isLoading: false, error: message });
    }
  },

  reset: () => {
    set({ data: null, isLoading: false, error: null, lastFetched: null });
  },
}));

// ── Derived hooks ──────────────────────────────────────────────────────────

/** Full entitlement state (data, loading, error). */
export function useEntitlements() {
  return useEntitlementStore((s) => ({
    data: s.data,
    isLoading: s.isLoading,
    error: s.error,
    fetchEntitlements: s.fetchEntitlements,
  }));
}

/** Whether a specific certification is accessible to the user (study mode). */
export function useCanAccessCert(certCode: string): boolean {
  const data = useEntitlementStore((s) => s.data);
  if (!data) return false;
  // Global premium users can access all certifications
  if (data.is_global_premium || data.is_admin) return true;
  return data.accessible_certifications.includes(certCode);
}

/** Whether the user can use exam mode for a specific certification. */
export function useCanExamMode(certCode: string): boolean {
  const data = useEntitlementStore((s) => s.data);
  if (!data) return false;
  if (data.is_global_premium || data.is_admin) return true;
  return data.can_exam_mode.includes(certCode);
}

/** Whether the user has a global premium subscription. */
export function useIsGlobalPremium(): boolean {
  return useEntitlementStore((s) => s.data?.is_global_premium ?? false);
}
